/**
 * AssistantChat — XEA's DeepSeek-powered chat surface.
 *
 * Two visual modes via the `variant` prop:
 *   - "bar"    Compact chat bar (used on the Dashboard) that expands into a
 *              full chat panel inline on focus / click. Designed to sit in
 *              the flow of the page, not float.
 *   - "embed"  Always-expanded panel (used inside the Video Generator custom
 *              section). Same internals, no collapse affordance.
 *
 * Both modes share one chat engine: session list on the left, messages on
 * the right, sticky composer at the bottom. Session history is persisted
 * server-side per user.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import {
  Sparkles,
  Send,
  Plus,
  Trash2,
  MessageSquare,
  Loader2,
  ChevronDown,
  ChevronUp,
  Bot,
  User as UserIcon,
  Paperclip,
  X as XIcon,
  Image as ImageIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10MB — mirrors backend cap
// Backend normalizes ANY common image format to JPEG (HEIC/AVIF/GIF/BMP/TIFF
// included) — the picker just needs to let them through. HEIC often arrives
// with an empty/octet-stream mime type, so we also match by extension.
const ACCEPT_ATTR = "image/*,.heic,.heif,.avif";
const IMAGE_EXT_RE = /\.(jpe?g|png|webp|gif|bmp|tiff?|heic|heif|avif)$/i;

const SUGGESTIONS = [
  "What's a strong concept for my next design?",
  "Write me a cinematic video prompt.",
  "Which realm should I use for a logo?",
  "Give me 3 caption variants for my latest render.",
];

export const AssistantChat = ({
  variant = "embed",
  contextHint = "",
  referenceImage = null,         // {image_url, description} | null — caller-provided ref
  onUsePrompt = null,            // (text) => void — show "Use as prompt" button on assistant replies
  usePromptLabel = "Use as video prompt",  // label for the "Use as prompt" button
  enableImageUpload = true,      // paperclip in composer for user-uploaded refs (on everywhere)
  onReferenceAttached = null,    // (refObj | null) => void — fires whenever the in-chat upload changes
}) => {
  const { token } = useAuth();
  const [expanded, setExpanded] = useState(variant === "embed");
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  // In-chat reference image attached via the paperclip composer button.
  // Persists across turns within a session so the user can iterate ("now
  // make it darker", "try the same vibe but vertical"). Cleared on session
  // switch or manual remove.
  const [attachedRef, setAttachedRef] = useState(null); // {image_url, description, filename} | null
  const [uploadingRef, setUploadingRef] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  // ─── Session list ──────────────────────────────────────────────────────
  const loadSessions = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/chat/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(r.data?.sessions || []);
      setSessionsLoaded(true);
    } catch (e) {
      // non-fatal
      setSessionsLoaded(true);
    }
  }, [token]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (expanded && !sessionsLoaded) loadSessions();
  }, [expanded, sessionsLoaded, loadSessions]);

  // ─── Pick a session ────────────────────────────────────────────────────
  const openSession = async (sessionId) => {
    setActiveSessionId(sessionId);
    setMessages([]);
    setLoadingSession(true);
    try {
      const r = await axios.get(`${API}/chat/sessions/${sessionId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(r.data?.messages || []);
    } catch (e) {
      toast.error("Couldn't load that conversation");
    } finally {
      setLoadingSession(false);
    }
  };

  const startNewSession = () => {
    setActiveSessionId(null);
    setMessages([]);
    setInput("");
    // Drop any attached reference when leaving the session — a brand-new
    // chat shouldn't inherit the previous brief's mood board.
    setAttachedRef(null);
    onReferenceAttached?.(null);
  };

  // ─── In-chat reference image upload ────────────────────────────────────
  const onPickFile = () => fileInputRef.current?.click();

  const uploadRef = async (file) => {
    if (!file) return;
    const looksLikeImage =
      (file.type || "").startsWith("image/") || IMAGE_EXT_RE.test(file.name || "");
    if (!looksLikeImage) {
      toast.error("That doesn't look like an image — any picture format works (JPEG, PNG, HEIC, WebP, GIF…).");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error(`Too large (max ${Math.round(MAX_UPLOAD_BYTES / 1024 / 1024)}MB)`);
      return;
    }
    setUploadingRef(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/chat/upload-reference`, fd, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      const ref = {
        image_url: r.data?.image_url,
        description: r.data?.description || "",
        filename: r.data?.filename,
      };
      setAttachedRef(ref);
      onReferenceAttached?.(ref);
      toast.success("Reference attached — describe the vibe you want next");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Couldn't upload that image";
      toast.error(detail);
    } finally {
      setUploadingRef(false);
      // Reset the input so re-picking the same file still fires onChange
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const clearAttachedRef = () => {
    setAttachedRef(null);
    onReferenceAttached?.(null);
  };

  const deleteSession = async (sessionId, e) => {
    e?.stopPropagation();
    if (!confirm("Delete this conversation?")) return;
    try {
      await axios.delete(`${API}/chat/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions((s) => s.filter((x) => x.id !== sessionId));
      if (activeSessionId === sessionId) startNewSession();
    } catch (e) {
      toast.error("Couldn't delete");
    }
  };

  // ─── Send a message ────────────────────────────────────────────────────
  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || sending) return;

    const optimisticUser = {
      id: `tmp-${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimisticUser]);
    setInput("");
    setSending(true);

    try {
      const payload = {
        session_id: activeSessionId,
        message: text,
      };
      // Prefer the in-chat upload, fall back to the caller-provided
      // reference (used by Video Generator custom flow).
      const ref = attachedRef || referenceImage;
      if (ref?.image_url) {
        payload.reference_image_url = ref.image_url;
        payload.reference_description = ref.description || "";
      }
      const r = await axios.post(
        `${API}/chat/message`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const newSessionId = r.data?.session_id;
      const reply = r.data?.reply || "";

      setMessages((m) => [
        ...m,
        {
          id: `r-${Date.now()}`,
          role: "assistant",
          content: reply,
          created_at: new Date().toISOString(),
        },
      ]);

      if (!activeSessionId && newSessionId) {
        setActiveSessionId(newSessionId);
        // refresh sidebar so the new session shows up
        loadSessions();
      } else if (activeSessionId) {
        // bump last_message_at order
        loadSessions();
      }
    } catch (e) {
      const detail =
        e?.response?.data?.detail || e?.message || "Assistant unavailable";
      toast.error(detail);
      // roll back optimistic message
      setMessages((m) => m.filter((x) => x.id !== optimisticUser.id));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  // ─── Auto-scroll to bottom on new message ──────────────────────────────
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // ═══════════════ Render: collapsed bar (dashboard) ═════════════════════
  if (variant === "bar" && !expanded) {
    return (
      <button
        type="button"
        onClick={() => setExpanded(true)}
        data-testid="assistant-chat-bar"
        className="group w-full text-left flex items-center gap-4 p-5 rounded-3xl bg-white border border-ink-200/70 hover:border-ink-950 hover:shadow-elevated transition-all"
      >
        <span className="relative inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-ink-950">
          <Sparkles className="w-5 h-5 text-citrus-500" />
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-coral-500 ring-2 ring-white" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-coral-500">
            XEA Assistant · free
          </p>
          <p className="text-base font-display font-bold text-ink-950 tracking-tight mt-0.5 truncate">
            Ask me anything about your store, videos, or next post…
          </p>
        </div>
        <span className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink-100 text-xs font-bold uppercase tracking-[0.18em] text-ink-700 group-hover:bg-ink-950 group-hover:text-white transition-colors">
          Open <ChevronDown className="w-3 h-3" />
        </span>
      </button>
    );
  }

  // ═══════════════ Render: full chat panel ═══════════════════════════════
  const activeSession = sessions.find((s) => s.id === activeSessionId);
  const containerCls =
    variant === "bar"
      ? "rounded-3xl bg-white border border-ink-200/70 shadow-elevated overflow-hidden"
      : "rounded-3xl bg-white border border-ink-200/70 overflow-hidden";

  return (
    <motion.div
      initial={variant === "bar" ? { opacity: 0, y: -8 } : false}
      animate={{ opacity: 1, y: 0 }}
      className={containerCls}
      data-testid="assistant-chat-panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-5 py-4 bg-ink-950 text-white">
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white/10">
            <Sparkles className="w-4 h-4 text-citrus-500" />
          </span>
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-citrus-500">
              XEA Assistant
            </p>
            <p className="text-sm font-semibold truncate">
              {activeSession?.title || "New conversation"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={startNewSession}
            data-testid="assistant-new-chat-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-bold uppercase tracking-[0.18em] transition-colors"
          >
            <Plus className="w-3 h-3" /> New
          </button>
          {variant === "bar" && (
            <button
              type="button"
              onClick={() => setExpanded(false)}
              data-testid="assistant-collapse-btn"
              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
              aria-label="Collapse"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Body: sessions sidebar + thread */}
      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] min-h-[420px] max-h-[560px]">
        {/* Sessions */}
        <aside
          className="hidden md:flex flex-col border-r border-ink-200/70 bg-ink-50/40 max-h-[560px]"
          data-testid="assistant-sessions"
        >
          <div className="px-4 py-3 border-b border-ink-200/70">
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500">
              History
            </p>
          </div>
          <div className="flex-1 overflow-y-auto py-2">
            {sessions.length === 0 ? (
              <p className="px-4 py-6 text-xs text-ink-500 leading-relaxed">
                Your conversations land here once you start one.
              </p>
            ) : (
              sessions.map((s) => {
                const active = s.id === activeSessionId;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => openSession(s.id)}
                    data-testid={`assistant-session-${s.id}`}
                    className={`group w-full text-left px-4 py-2.5 flex items-center gap-2 transition-colors ${
                      active
                        ? "bg-coral-500/10 border-l-2 border-coral-500"
                        : "hover:bg-ink-100/60 border-l-2 border-transparent"
                    }`}
                  >
                    <MessageSquare
                      className={`w-3.5 h-3.5 shrink-0 ${
                        active ? "text-coral-500" : "text-ink-400"
                      }`}
                    />
                    <span className="text-xs font-medium text-ink-950 truncate flex-1">
                      {s.title || "Untitled"}
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => deleteSession(s.id, e)}
                      className="opacity-0 group-hover:opacity-100 text-ink-400 hover:text-coral-500 transition-opacity"
                      data-testid={`assistant-delete-${s.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        {/* Thread */}
        <div className="flex flex-col max-h-[560px]">
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
            data-testid="assistant-messages"
          >
            {loadingSession ? (
              <div className="flex items-center justify-center py-12 text-ink-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : messages.length === 0 ? (
              <EmptyState onPick={(t) => send(t)} contextHint={contextHint} />
            ) : (
              <AnimatePresence initial={false}>
                {messages.map((m) => (
                  <MessageBubble
                    key={m.id}
                    role={m.role}
                    content={m.content}
                    onUsePrompt={onUsePrompt}
                    usePromptLabel={usePromptLabel}
                  />
                ))}
              </AnimatePresence>
            )}
            {sending && (
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-ink-950 text-citrus-500 shrink-0">
                  <Bot className="w-4 h-4" />
                </span>
                <div className="px-4 py-3 rounded-2xl bg-ink-100 inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-ink-400 animate-bounce" />
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-ink-400 animate-bounce"
                    style={{ animationDelay: "120ms" }}
                  />
                  <span
                    className="w-1.5 h-1.5 rounded-full bg-ink-400 animate-bounce"
                    style={{ animationDelay: "240ms" }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-ink-200/70 bg-white px-4 py-3"
          >
            {/* Attached reference preview — only shown when the user
                uploaded a picture in-chat. Click X to detach. */}
            {attachedRef && (
              <div
                className="mb-2 flex items-center gap-3 p-2 rounded-xl bg-ink-50 border border-ink-200"
                data-testid="assistant-attached-ref"
              >
                <img
                  src={attachedRef.image_url}
                  alt="Reference"
                  className="w-12 h-12 rounded-lg object-cover border border-ink-200"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-coral-500">
                    Reference attached
                  </p>
                  <p className="text-xs text-ink-700 truncate">
                    {attachedRef.description
                      ? attachedRef.description.slice(0, 90) + (attachedRef.description.length > 90 ? "…" : "")
                      : "Tell me how you want your product styled like this."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={clearAttachedRef}
                  data-testid="assistant-clear-ref"
                  className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-white border border-ink-200 hover:bg-coral-500 hover:text-white hover:border-coral-500 transition-colors"
                  aria-label="Remove reference"
                >
                  <XIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
            <div className="flex items-end gap-2">
              {enableImageUpload && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT_ATTR}
                    onChange={(e) => uploadRef(e.target.files?.[0])}
                    className="hidden"
                    data-testid="assistant-file-input"
                  />
                  <button
                    type="button"
                    onClick={onPickFile}
                    disabled={uploadingRef}
                    data-testid="assistant-attach-btn"
                    aria-label="Attach reference image"
                    title="Attach a reference picture — I'll style your product to match"
                    className="!h-11 !w-11 inline-flex items-center justify-center rounded-2xl border border-ink-200 bg-ink-50 hover:bg-ink-950 hover:text-citrus-500 hover:border-ink-950 text-ink-700 shrink-0 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {uploadingRef ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : attachedRef ? (
                      <ImageIcon className="w-4 h-4 text-coral-500" />
                    ) : (
                      <Paperclip className="w-4 h-4" />
                    )}
                  </button>
                </>
              )}
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder={
                  contextHint ||
                  "Ask about prompts, concepts, captions, or how anything in XEA works…"
                }
                rows={1}
                data-testid="assistant-input"
                className="flex-1 resize-none min-h-[44px] max-h-[140px] bg-ink-50 border-ink-200 focus:border-ink-950 text-ink-950 text-sm rounded-2xl"
              />
              <Button
                type="submit"
                disabled={(!input.trim() && !attachedRef) || sending}
                data-testid="assistant-send-btn"
                className="!h-11 !w-11 !p-0 !rounded-2xl shrink-0"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </motion.div>
  );
};

// ─── Sub-components ──────────────────────────────────────────────────────

const MessageBubble = ({ role, content, onUsePrompt = null, usePromptLabel = "Use as video prompt" }) => {
  const isUser = role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex items-start gap-3 ${isUser ? "flex-row-reverse" : ""}`}
    >
      <span
        className={`inline-flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
          isUser ? "bg-coral-500 text-white" : "bg-ink-950 text-citrus-500"
        }`}
      >
        {isUser ? <UserIcon className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </span>
      <div className={`max-w-[80%] flex flex-col ${isUser ? "items-end" : "items-start"}`}>
        <div
          className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
            isUser
              ? "bg-coral-500 text-white rounded-tr-md"
              : "bg-ink-100 text-ink-950 rounded-tl-md"
          }`}
        >
          {content}
        </div>
        {!isUser && onUsePrompt && content.length > 20 && (
          <button
            type="button"
            onClick={() => onUsePrompt(content)}
            data-testid="assistant-use-as-prompt"
            className="mt-1.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-ink-950 hover:bg-coral-500 text-white text-[10px] font-bold uppercase tracking-[0.18em] transition-colors"
          >
            {usePromptLabel}
          </button>
        )}
      </div>
    </motion.div>
  );
};

const EmptyState = ({ onPick, contextHint }) => {
  const suggestions = contextHint
    ? [
        "Suggest a hook for this video idea.",
        "Rewrite this prompt to feel more cinematic.",
        "What music vibe fits this scene?",
        "Give me 3 caption variants for this render.",
      ]
    : SUGGESTIONS;
  return (
    <div className="flex flex-col items-center justify-center text-center py-6">
      <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-ink-950 text-citrus-500 mb-4">
        <Sparkles className="w-6 h-6" />
      </span>
      <h3 className="font-display font-black text-2xl tracking-[-0.03em] text-ink-950">
        How can I help?
      </h3>
      <p className="text-sm text-ink-500 mt-2 max-w-sm">
        I know your recent generations and style. Try one of
        these to get started.
      </p>
      <div className="grid sm:grid-cols-2 gap-2 mt-6 w-full max-w-lg">
        {suggestions.map((s, i) => (
          <button
            key={s}
            type="button"
            onClick={() => onPick(s)}
            data-testid={`assistant-suggestion-${i}`}
            className="text-left text-xs font-medium text-ink-700 px-3 py-2.5 rounded-xl bg-ink-50 border border-ink-200/70 hover:border-ink-950 hover:bg-white transition-all"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
};

export default AssistantChat;
